// app/javascript/controllers/operational_map_controller.js
import { Controller } from "@hotwired/stimulus"
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

export default class extends Controller {
  static targets = ["map", "loading", "regionFilters", "typeFilters", "topicFilters"]
  static values = {
    apiKey: String,
    areasUrl: String
  }

  connect() {
    if (this.hasApiKeyValue) {
      mapboxgl.accessToken = this.apiKeyValue
      this.initializeMap()
    } else {
      console.error("Mapbox API key is missing")
    }
  }

  async initializeMap() {
    this.map = new mapboxgl.Map({
      container: this.mapTarget,
      style: 'mapbox://styles/mapbox/navigation-day-v1',
      center: [42.590275, 11.825138], // Centre de Djibouti
      zoom: 7
    })

    this.map.addControl(new mapboxgl.NavigationControl(), 'top-right')
    this.map.addControl(new mapboxgl.FullscreenControl(), 'top-right')

    this.map.on('load', () => {
      this.loadOperationalAreas()
    })
  }

  async loadOperationalAreas() {
    try {
      const response = await fetch(this.areasUrlValue)
      const data = await response.json()
      
      if (!response.ok) {
        throw new Error("Failed to load operational areas")
      }

      this.areas = data
      this.addOperationalAreasToMap()
      this.setupRegionFilters()
      this.hideLoading()
    } catch (error) {
      console.error('Error loading operational areas:', error)
      this.hideLoading()
    }
  }

  addOperationalAreasToMap() {
    // Ajouter la source de données
    this.map.addSource('operational-areas', {
      type: 'geojson',
      data: this.areasToGeoJson(),
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50
    })

    // Ajouter la couche des clusters
    this.map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'operational-areas',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#10218b', // Bleu surfaceadmin pour les petits clusters
          10, '#439e34', // Vert surfacemembre pour les clusters moyens
          30, '#000000' // Noir pour les grands clusters
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          20,
          10, 30,
          30, 40
        ]
      }
    })

    // La couche de texte pour les clusters reste la même
    this.map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'operational-areas',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      },
      paint: {
        'text-color': '#ffffff' // Couleur blanche pour le texte
      }
    })

    // Modifier la couche des points individuels
    this.map.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'operational-areas',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': '#10218b', // Bleu surfaceadmin pour les points individuels
        'circle-radius': 8,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#fff'
      }
    })

    // Gérer le clic sur un cluster
    this.map.on('click', 'clusters', (e) => {
      const features = this.map.queryRenderedFeatures(e.point, { layers: ['clusters'] })
      const clusterId = features[0].properties.cluster_id
      
      this.map.getSource('operational-areas').getClusterExpansionZoom(
        clusterId,
        (err, zoom) => {
          if (err) return
          
          this.map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom
          })
        }
      )
    })

    // Gérer le clic sur un point individuel
    this.map.on('click', 'unclustered-point', (e) => {
      const coordinates = e.features[0].geometry.coordinates.slice()
      const infoWindow = e.features[0].properties.info_window
      
      new mapboxgl.Popup()
        .setLngLat(coordinates)
        .setHTML(infoWindow)
        .addTo(this.map)
    })

    // Changer le curseur au survol des points
    this.map.on('mouseenter', 'clusters', () => {
      this.map.getCanvas().style.cursor = 'pointer'
    })
    
    this.map.on('mouseleave', 'clusters', () => {
      this.map.getCanvas().style.cursor = ''
    })
    
    this.map.on('mouseenter', 'unclustered-point', () => {
      this.map.getCanvas().style.cursor = 'pointer'
    })
    
    this.map.on('mouseleave', 'unclustered-point', () => {
      this.map.getCanvas().style.cursor = ''
    })
  }

  setupRegionFilters() {
    // Obtenir les régions uniques
    const regions = [...new Set(this.areas.map(area => area.admin1))]
    
    // Créer les éléments de filtre
    this.regionFiltersTarget.innerHTML = `
      <div class="flex items-center mb-1">
        <input type="checkbox" id="filter-all" class="mr-2 custom-checkbox" checked data-action="change->operational-map#filterAllRegions">
        <label for="filter-all" class="text-sm">Toutes les régions</label>
      </div>
      ${regions.map((region, index) => `
        <div class="flex items-center">
          <input type="checkbox" id="filter-region-${index}" class="mr-2 custom-checkbox" checked data-action="change->operational-map#filterRegion" data-region="${region}">
          <label for="filter-region-${index}" class="text-sm">${region}</label>
        </div>
      `).join('')}
    `
  }

  // Filtre pour toutes les régions
  filterAllRegions(e) {
    const checked = e.target.checked
    
    // Mettre à jour tous les autres checkboxes de région
    this.regionFiltersTarget.querySelectorAll('input[type="checkbox"]:not(#filter-all)').forEach(checkbox => {
      checkbox.checked = checked
    })
    
    this.updateMapFilter()
  }

  // Filtre pour une région spécifique
  filterRegion() {
    // Vérifier si toutes les régions sont cochées
    const allChecked = Array.from(
      this.regionFiltersTarget.querySelectorAll('input[type="checkbox"]:not(#filter-all)')
    ).every(checkbox => checkbox.checked)
    
    // Mettre à jour le checkbox "Toutes les régions"
    this.regionFiltersTarget.querySelector('#filter-all').checked = allChecked
    
    this.updateMapFilter()
  }

  // Filtre pour tous les types d'organisations
  filterAllTypes(e) {
    const checked = e.target.checked
    
    // Mettre à jour tous les autres checkboxes de type
    this.typeFiltersTarget.querySelectorAll('input[type="checkbox"]:not(#type-all)').forEach(checkbox => {
      checkbox.checked = checked
    })
    
    this.updateMapFilter()
  }

  // Filtre pour un type d'organisation spécifique
  filterType() {
    // Vérifier si tous les types sont cochés
    const allChecked = Array.from(
      this.typeFiltersTarget.querySelectorAll('input[type="checkbox"]:not(#type-all)')
    ).every(checkbox => checkbox.checked)
    
    // Mettre à jour le checkbox "Tous les types"
    this.typeFiltersTarget.querySelector('#type-all').checked = allChecked
    
    this.updateMapFilter()
  }

  // Filtre pour toutes les thématiques
  filterAllTopics(e) {
    const checked = e.target.checked
    
    // Mettre à jour tous les autres checkboxes de thématique
    this.topicFiltersTarget.querySelectorAll('input[type="checkbox"]:not(#topic-all)').forEach(checkbox => {
      checkbox.checked = checked
    })
    this.updateMapFilter()
  }

  // Filtre pour une thématique spécifique
  filterTopic() {
    // Vérifier si toutes les thématiques sont cochées
    const allChecked = Array.from(
      this.topicFiltersTarget.querySelectorAll('input[type="checkbox"]:not(#topic-all)')
    ).every(checkbox => checkbox.checked)
    
    // Mettre à jour le checkbox "Toutes les thématiques"
    this.topicFiltersTarget.querySelector('#topic-all').checked = allChecked
    
    this.updateMapFilter()
  }

  // Réinitialiser tous les filtres
  resetFilters() {
    // Cocher tous les checkboxes de régions
    this.regionFiltersTarget.querySelector('#filter-all').checked = true
    this.regionFiltersTarget.querySelectorAll('input[type="checkbox"]:not(#filter-all)').forEach(checkbox => {
      checkbox.checked = true
    })
    
    // Cocher tous les checkboxes de types
    this.typeFiltersTarget.querySelector('#type-all').checked = true
    this.typeFiltersTarget.querySelectorAll('input[type="checkbox"]:not(#type-all)').forEach(checkbox => {
      checkbox.checked = true
    })
    
    // Cocher tous les checkboxes de thématiques
    this.topicFiltersTarget.querySelector('#topic-all').checked = true
    this.topicFiltersTarget.querySelectorAll('input[type="checkbox"]:not(#topic-all)').forEach(checkbox => {
      checkbox.checked = true
    })
    
    // Mettre à jour la carte
    this.updateMapFilter()
  }

  // Dans updateMapFilter()
  updateMapFilter() {
    try {
      const allRegionsChecked = this.regionFiltersTarget.querySelector('#filter-all').checked;
      const allTypesChecked = this.typeFiltersTarget.querySelector('#type-all').checked;
      const allTopicsChecked = this.topicFiltersTarget.querySelector('#topic-all').checked;

      if (allRegionsChecked && allTypesChecked && allTopicsChecked) {
        this.map.getSource('operational-areas').setData(this.areasToGeoJson());
        return;
      }

      const selectedRegions = allRegionsChecked
        ? [...new Set(this.areas.map(area => area.admin1))]
        : Array.from(
            this.regionFiltersTarget.querySelectorAll('input[type="checkbox"]:not(#filter-all):checked')
          ).map(checkbox => checkbox.dataset.region);

      const selectedTypeIds = allTypesChecked
        ? [...new Set(this.areas.map(area => area.organization_type_id))]
        : Array.from(
            this.typeFiltersTarget.querySelectorAll('input[type="checkbox"]:not(#type-all):checked')
          ).map(checkbox => parseInt(checkbox.dataset.typeId));

      const selectedTopicIds = allTopicsChecked
        ? []  // Si tous les sujets sont sélectionnés, pas besoin de filtrer
        : Array.from(
            this.topicFiltersTarget.querySelectorAll('input[type="checkbox"]:not(#topic-all):checked')
          ).map(checkbox => parseInt(checkbox.dataset.topicId));

      const filteredAreas = this.areas.filter(area => {
        const regionMatch = selectedRegions.includes(area.admin1);
        const typeMatch = selectedTypeIds.includes(area.organization_type_id);
        
        // Vérifier si tous les topics sont sélectionnés ou si l'area a au moins un topic qui correspond (via intervention_sector_ids uniquement)
        const topicMatch = allTopicsChecked || (area.intervention_sector_ids || []).some(id => 
          selectedTopicIds.includes(id)
        );

        return regionMatch && typeMatch && topicMatch;
      });

      if (filteredAreas.length === 0) {
        this.map.getSource('operational-areas').setData({
          type: 'FeatureCollection',
          features: []
        });
      } else {
        this.map.getSource('operational-areas').setData(this.areasToGeoJson(filteredAreas));
      }
    } catch (error) {
      console.error('Error in updateMapFilter:', error);
    }
  }

  areasToGeoJson(areas = null) {
    const areaData = areas || this.areas;
    
    return {
      type: 'FeatureCollection',
      features: areaData.map(area => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [parseFloat(area.longitude), parseFloat(area.latitude)]
        },
        properties: {
          id: area.id,
          organization_name: area.organization_name,
          organization_id: area.organization_id,
          organization_type: area.organization_type,
          organization_type_id: area.organization_type_id,
          expertise_domain_ids: area.expertise_domain_ids || [],
          intervention_sector_ids: area.intervention_sector_ids || [],
          admin0: area.admin0,
          admin1: area.admin1,
          admin2: area.admin2 || '',
          info_window: area.info_window
        }
      }))
    };
  }
  
  hideLoading() {
    this.loadingTarget.classList.add('hidden')
  }
}